import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { payrollSettingsService } from "./payroll.services";
import { PAYROLL_KEYS } from "./payroll.keys";
import { CreatePayrollSettingDto, UpdatePayrollSettingDto, UpsertPayrollSettingDto } from "~/types/payroll";

// Get all payroll settings
export const useGetPayrollSettings = () => {
  return useQuery({
    queryKey: PAYROLL_KEYS.lists(),
    queryFn: payrollSettingsService.getAll,
  });
};

// Get payroll setting by ID
export const useGetPayrollSetting = (id: string) => {
  return useQuery({
    queryKey: PAYROLL_KEYS.detail(id),
    queryFn: () => payrollSettingsService.getById(id),
    enabled: !!id,
  });
};

// Get payroll setting by key
export const useGetPayrollSettingByKey = (key: string) => {
  return useQuery({
    queryKey: PAYROLL_KEYS.byKey(key),
    queryFn: () => payrollSettingsService.getByKey(key),
    enabled: !!key,
  });
};

// Get settings as a map
export const useGetPayrollSettingsMap = () => {
  return useQuery({
    queryKey: PAYROLL_KEYS.settingsMap(),
    queryFn: payrollSettingsService.getSettingsMap,
  });
};

// Create payroll setting mutation
export const useCreatePayrollSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePayrollSettingDto) => payrollSettingsService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYROLL_KEYS.all });
      message.success("Payroll setting created successfully");
    },
    onError: () => {
      message.error("Failed to create payroll setting");
    },
  });
};

// Update payroll setting mutation
export const useUpdatePayrollSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePayrollSettingDto }) =>
      payrollSettingsService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYROLL_KEYS.all });
      message.success("Payroll setting updated successfully");
    },
    onError: () => {
      message.error("Failed to update payroll setting");
    },
  });
};

// Upsert payroll setting mutation
export const useUpsertPayrollSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, dto }: { key: string; dto: UpsertPayrollSettingDto }) =>
      payrollSettingsService.upsertByKey(key, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYROLL_KEYS.all });
      message.success("Payroll setting saved successfully");
    },
    onError: () => {
      message.error("Failed to save payroll setting");
    },
  });
};

// Delete payroll setting mutation
export const useDeletePayrollSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => payrollSettingsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYROLL_KEYS.all });
      message.success("Payroll setting deleted successfully");
    },
    onError: () => {
      message.error("Failed to delete payroll setting");
    },
  });
};
